import React, { useEffect, useMemo, useState } from "react";
import authService from "../../services/AuthService.js";
import { useLanguage } from "../../contexts/LanguageContext";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const glassPanelClass =
  "bg-white dark:bg-gray-800/50 backdrop-blur-lg rounded-3xl shadow-xl border border-slate-200/50 dark:border-slate-600/50";
const cardClass =
  "bg-white dark:bg-gray-800/50 backdrop-blur-lg rounded-2xl border border-slate-200/50 dark:border-slate-600/50";
const inputClass =
  "w-full border border-slate-300/50 dark:border-slate-300/30 bg-ink-50 dark:bg-white/10 text-ink-900 dark:text-white placeholder-ink-400 dark:placeholder-white/60 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-300/40";
const btnPrimaryClass =
  "px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50";
const btnPrimarySmallClass =
  "px-3 py-1 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs hover:from-emerald-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50";
const btnSecondaryClass =
  "px-3 py-1 rounded-lg bg-ink-100 text-ink-700 text-xs hover:bg-ink-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 transition-colors";
const btnWarningClass =
  "px-3 py-1 rounded-lg bg-amber-500 text-white text-xs hover:bg-amber-600 transition-colors";
const btnDangerClass =
  "px-3 py-1 rounded-lg bg-red-600 text-white text-xs hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const modalCardClass = `${glassPanelClass} w-full max-w-4xl p-6 md:p-7`;
const btnTabClass =
  "px-4 py-2 rounded-xl text-sm font-medium transition-colors";

const AdminDashboard = () => {
  const { t } = useLanguage();
  const tt = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipeSearch, setRecipeSearch] = useState("");
  const [recentComments, setRecentComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "user",
  });
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
  });

  const currentUser = JSON.parse(localStorage.getItem("user") || "null") || {};

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await authService.getAllUsersAdmin();
      setUsers(response?.data || []);
      setError("");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        tt("unableLoadUsers", "Impossible de charger les utilisateurs.");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      setRecipesLoading(true);
      const response = await authService.getRecipesAdmin({
        page: 1,
        limit: 20,
        search: recipeSearch || undefined,
      });
      setRecipes(response?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt("unableLoadRecipes", "Impossible de charger les recettes."),
      );
    } finally {
      setRecipesLoading(false);
    }
  };

  const loadRecentComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await authService.getRecentCommentsAdmin(12);
      setRecentComments(response?.comments || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt("unableLoadRecentActivity", "Impossible de charger l'activité récente."),
      );
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeTab === "content") {
      loadRecipes();
    }
    if (activeTab === "activity") {
      loadRecentComments();
    }
  }, [activeTab]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = String(searchTerm || "")
      .trim()
      .toLowerCase();
    if (!normalizedQuery) return users;

    return users.filter((item) => {
      const fullName =
        `${item.first_name || ""} ${item.last_name || ""}`.trim();
      return [item.username, item.email, fullName, item.role]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    });
  }, [searchTerm, users]);

  const onCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const onEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      await authService.createUserAdmin({
        ...createForm,
        username: createForm.username.trim(),
        email: createForm.email.trim(),
      });
      setSuccess(tt("userCreatedSuccess", "Utilisateur créé avec succès."));
      setCreateForm({
        username: "",
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        role: "user",
      });
      setShowCreatePanel(false);
      await loadUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt("unableCreateUser", "Impossible de créer l'utilisateur."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username || "",
      email: user.email || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      role: user.role || "user",
    });
    setShowEditModal(true);
    setError("");
    setSuccess("");
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditForm({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      role: "user",
    });
  };

  const saveEdit = async (userId) => {
    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      await authService.updateUserAdmin(userId, {
        ...editForm,
        username: editForm.username.trim(),
        email: editForm.email.trim(),
      });
      setSuccess(tt("userUpdated", "Utilisateur mis à jour."));
      closeEditModal();
      await loadUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt("unableUpdateUser", "Impossible de modifier l'utilisateur."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmed = window.confirm(
      `Supprimer ${user.username} (${user.email}) ? Cette action est irreversible.`,
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      await authService.deleteUserAdmin(user.id);
      setSuccess(tt("userDeleted", "Utilisateur supprimé."));
      await loadUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt("unableDeleteUser", "Impossible de supprimer l'utilisateur."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPasswordInput("");
    setShowPasswordModal(true);
    setError("");
    setSuccess("");
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setNewPasswordInput("");
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!newPasswordInput) return;

    if (newPasswordInput.length < 6) {
      setError(
        tt(
          "newPasswordMinLength",
          "Le nouveau mot de passe doit contenir au moins 6 caractères.",
        ),
      );
      return;
    }

    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      await authService.resetUserPasswordAdmin(
        selectedUser.id,
        newPasswordInput,
      );
      setSuccess(
        `${tt("passwordUpdatedFor", "Mot de passe mis à jour pour")} ${selectedUser.username}.`,
      );
      closePasswordModal();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          tt(
            "unableChangeUserPassword",
            "Impossible de changer le mot de passe de l'utilisateur.",
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRecipe = async (recipe) => {
    const confirmed = window.confirm(
      `Supprimer la recette "${recipe.title}" ?`,
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      await authService.deleteRecipeAdmin(recipe.id);
      setSuccess(tt("recipeDeletedSuccess", "Recette supprimée avec succès."));
      await loadRecipes();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          tt("unableDeleteRecipe", "Impossible de supprimer la recette."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportUsersCsv = () => {
    const headers = [
      "id",
      "username",
      "email",
      "role",
      "first_name",
      "last_name",
    ];
    const rows = users.map((u) =>
      [u.id, u.username, u.email, u.role, u.first_name || "", u.last_name || ""]
        .map((value) => `"${String(value).replaceAll('"', '""')}"`)
        .join(","),
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "users-export.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen w-full">
      <div className="max-w-[1800px] mx-auto">
        <section
          className={`${glassPanelClass} px-6 py-6 md:px-8 md:py-7 mb-6`}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                {tt("admin", "Administration")}
              </p>
              <h1 className="text-3xl font-bold text-ink-900 dark:text-white mt-1">
                {tt("adminUserManagementTitle", "Espace Gestion Utilisateurs")}
              </h1>
              <p className="text-ink-600 dark:text-white/80 mt-2">
                {tt(
                  "adminUserManagementSubtitle",
                  "Créez, modifiez, supprimez des comptes et réinitialisez les mots de passe en un seul endroit.",
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCreatePanel(true)}
              className={btnPrimaryClass}
            >
              {tt("newUser", "Nouveau utilisateur")}
            </button>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("users")}
              className={`${btnTabClass} ${
                activeTab === "users"
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/30 dark:text-emerald-100 dark:border-emerald-300/40"
                  : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20"
              }`}
            >
              {tt("manageUsers", "Gestion utilisateurs")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("content")}
              className={`${btnTabClass} ${
                activeTab === "content"
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/30 dark:text-emerald-100 dark:border-emerald-300/40"
                  : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20"
              }`}
            >
              {tt("manageRecipes", "Gestion recettes")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("activity")}
              className={`${btnTabClass} ${
                activeTab === "activity"
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/30 dark:text-emerald-100 dark:border-emerald-300/40"
                  : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20"
              }`}
            >
              {tt("recentActivity", "Activité récente")}
            </button>
            <button
              type="button"
              onClick={exportUsersCsv}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20"
            >
              {tt("exportUsersCsv", "Export users CSV")}
            </button>
          </div>
        </section>

        {!!success && (
          <div className="mb-6 bg-emerald-500/15 border border-emerald-300/30 text-emerald-200 rounded-xl p-4">
            {success}
          </div>
        )}

        {!!error && (
          <div className="mb-6 bg-red-500/15 border border-red-300/30 text-red-200 rounded-xl p-4">
            {error}
          </div>
        )}

        {isLoading && activeTab === "users" && (
          <div className={`${glassPanelClass} p-6 text-white/80`}>
            {tt("loadingAdminData", "Chargement des donnees admin...")}
          </div>
        )}

        {!isLoading && activeTab === "users" && (
          <div className={`${glassPanelClass} overflow-hidden`}>
            <div className="px-6 py-4 border-b border-ink-900/10 dark:border-white/20 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-ink-900 dark:text-white">
                  {tt("users", "Utilisateurs")}
                </h2>
                <span className="text-sm text-ink-500 dark:text-white/70">
                  {filteredUsers.length} {tt("on", "sur")} {users.length}{" "}
                  {tt("accounts", "comptes")}
                </span>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={tt(
                  "searchByNameEmailRole",
                  "Rechercher (nom, email, role...)",
                )}
                className={`${inputClass} md:w-80`}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-ink-50 dark:bg-white/10 text-ink-600 dark:text-white/80 text-sm">
                  <tr>
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">{tt("username", "Username")}</th>
                    <th className="px-6 py-3">{tt("email", "Email")}</th>
                    <th className="px-6 py-3">
                      {tt("fullName", "Nom complet")}
                    </th>
                    <th className="px-6 py-3">{tt("role", "Role")}</th>
                    <th className="px-6 py-3">
                      {tt("registration", "Inscription")}
                    </th>
                    <th className="px-6 py-3">{tt("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((item) => (
                    <tr key={item.id} className="border-t border-white/10">
                      <td className="px-6 py-3 text-ink-700 dark:text-white/90">{item.id}</td>
                      <td className="px-6 py-3 text-ink-700 dark:text-white/90">
                        {item.username}
                      </td>
                      <td className="px-6 py-3 text-ink-700 dark:text-white/90">{item.email}</td>
                      <td className="px-6 py-3 text-ink-700 dark:text-white/90">
                        {`${item.first_name || ""} ${item.last_name || ""}`.trim() ||
                          "-"}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            String(item.role || "").toLowerCase() === "admin"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200"
                              : "bg-ink-100 text-ink-700 dark:bg-white/10 dark:text-white/80"
                          }`}
                        >
                          {item.role || "user"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-ink-600 dark:text-white/80">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className={btnPrimarySmallClass}
                          >
                            {tt("edit", "Modifier")}
                          </button>
                          <button
                            type="button"
                            onClick={() => openPasswordModal(item)}
                            className={btnWarningClass}
                          >
                            {tt("changePasswordShort", "Changer MDP")}
                          </button>
                          <button
                            type="button"
                            disabled={
                              Number(item.id) === Number(currentUser?.id)
                            }
                            onClick={() => handleDeleteUser(item)}
                            className={btnDangerClass}
                          >
                            {tt("delete", "Supprimer")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-8 text-center text-ink-500 dark:text-white/70"
                      >
                        {tt("noUserFound", "Aucun utilisateur trouvé.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "content" && (
          <div className={`${glassPanelClass} overflow-hidden`}>
            <div className="px-6 py-4 border-b border-ink-900/10 dark:border-white/20 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-ink-900 dark:text-white">
                  {tt("recipeModeration", "Modération des recettes")}
                </h2>
                <span className="text-sm text-ink-500 dark:text-white/70">
                  {recipes.length} {tt("recipesLoaded", "recettes chargées")}
                </span>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <input
                  type="text"
                  value={recipeSearch}
                  onChange={(event) => setRecipeSearch(event.target.value)}
                  placeholder={tt("searchRecipe", "Rechercher une recette...")}
                  className={`${inputClass} md:w-80`}
                />
                <button
                  type="button"
                  onClick={loadRecipes}
                  className={btnPrimarySmallClass}
                >
                  {tt("search", "Rechercher")}
                </button>
              </div>
            </div>
            {recipesLoading ? (
              <div className="p-6 text-ink-600 dark:text-white/80">
                {tt("loadingRecipes", "Chargement des recettes...")}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-ink-50 dark:bg-white/10 text-ink-600 dark:text-white/80 text-sm">
                    <tr>
                      <th className="px-6 py-3">ID</th>
                      <th className="px-6 py-3">{tt("title", "Titre")}</th>
                      <th className="px-6 py-3">{tt("author", "Auteur")}</th>
                      <th className="px-6 py-3">
                        {tt("difficulty", "Difficulté")}
                      </th>
                      <th className="px-6 py-3">{tt("date", "Date")}</th>
                      <th className="px-6 py-3">{tt("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map((recipe) => (
                      <tr key={recipe.id} className="border-t border-white/10">
                        <td className="px-6 py-3 text-ink-700 dark:text-white/90">{recipe.id}</td>
                        <td className="px-6 py-3 text-ink-700 dark:text-white/90">
                          {recipe.title}
                        </td>
                        <td className="px-6 py-3 text-ink-600 dark:text-white/80">
                          {recipe.username || tt("anonymous", "Anonyme")}
                        </td>
                        <td className="px-6 py-3 text-ink-600 dark:text-white/80">
                          {recipe.difficulty || "-"}
                        </td>
                        <td className="px-6 py-3 text-ink-600 dark:text-white/80">
                          {formatDate(recipe.created_at)}
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-2">
                            <a
                              href={`/recipe/${recipe.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className={btnSecondaryClass}
                            >
                              {tt("view", "Voir")}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteRecipe(recipe)}
                              disabled={isSubmitting}
                              className={btnDangerClass}
                            >
                              {tt("delete", "Supprimer")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {recipes.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-8 text-center text-ink-500 dark:text-white/70"
                        >
                          {tt("noRecipeFound", "Aucune recette trouvée.")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "activity" && (
          <div className={`${glassPanelClass} overflow-hidden`}>
            <div className="px-6 py-4 border-b border-ink-900/10 dark:border-white/20">
              <h2 className="text-xl font-semibold text-ink-900 dark:text-white">
                {tt("recentActivity", "Activité récente")}
              </h2>
              <p className="text-sm text-ink-500 dark:text-white/70 mt-1">
                {tt(
                  "recentCommentsCommunity",
                  "Derniers commentaires publiés sur la communauté.",
                )}
              </p>
            </div>
            {commentsLoading ? (
              <div className="p-6 text-ink-600 dark:text-white/80">
                {tt("loadingActivity", "Chargement de l'activité...")}
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {recentComments.map((comment) => (
                  <div key={comment.id} className="px-6 py-4">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <p className="text-ink-900 dark:text-white font-medium">
                        {comment.username ||
                          comment.first_name ||
                          tt("user", "Utilisateur")}
                      </p>
                      <span className="text-xs text-ink-400 dark:text-white/60">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-ink-600 dark:text-white/80 text-sm line-clamp-2">
                      {comment.comment_text || comment.commentText || "-"}
                    </p>
                  </div>
                ))}
                {recentComments.length === 0 && (
                  <div className="px-6 py-8 text-center text-ink-500 dark:text-white/70">
                    {tt("noRecentActivity", "Aucune activité récente.")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showCreatePanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
          onClick={() => setShowCreatePanel(false)}
        >
          <div
            className={modalCardClass}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-xl font-semibold text-ink-900 dark:text-white mb-1">
                  {tt("createUser", "Créer un utilisateur")}
                </h2>
                <p className="text-sm text-ink-500 dark:text-white/70">
                  {tt(
                    "createUserSubtitle",
                    "Formulaire rapide pour créer un compte user/admin.",
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreatePanel(false)}
                className={btnSecondaryClass}
              >
                {tt("close", "Fermer")}
              </button>
            </div>

            <form
              onSubmit={handleCreateUser}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <input
                type="text"
                name="username"
                value={createForm.username}
                onChange={onCreateChange}
                placeholder="Username"
                className={inputClass}
                required
              />
              <input
                type="email"
                name="email"
                value={createForm.email}
                onChange={onCreateChange}
                placeholder="Email"
                className={inputClass}
                required
              />
              <input
                type="password"
                name="password"
                value={createForm.password}
                onChange={onCreateChange}
                placeholder="Mot de passe (min 6)"
                className={inputClass}
                required
              />
              <input
                type="text"
                name="first_name"
                value={createForm.first_name}
                onChange={onCreateChange}
                placeholder="Prénom"
                className={inputClass}
              />
              <input
                type="text"
                name="last_name"
                value={createForm.last_name}
                onChange={onCreateChange}
                placeholder="Nom"
                className={inputClass}
              />
              <select
                name="role"
                value={createForm.role}
                onChange={onCreateChange}
                className={inputClass}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
              <div className="md:col-span-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreatePanel(false)}
                  className={btnSecondaryClass}
                >
                  {tt("cancel", "Annuler")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={btnPrimaryClass}
                >
                  {tt("create", "Créer")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
          onClick={closeEditModal}
        >
          <div
            className={modalCardClass}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-xl font-semibold text-ink-900 dark:text-white mb-1">
                  {tt("editUser", "Modifier utilisateur")}
                </h2>
                <p className="text-sm text-ink-500 dark:text-white/70">
                  {tt("updatingUserAccount", "Mise à jour du compte de")}{" "}
                  {selectedUser.username}
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className={btnSecondaryClass}
              >
                {tt("close", "Fermer")}
              </button>
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveEdit(selectedUser.id);
              }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <input
                type="text"
                name="username"
                value={editForm.username}
                onChange={onEditChange}
                placeholder="Username"
                className={inputClass}
                required
              />
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={onEditChange}
                placeholder="Email"
                className={inputClass}
                required
              />
              <select
                name="role"
                value={editForm.role}
                onChange={onEditChange}
                className={inputClass}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
              <input
                type="text"
                name="first_name"
                value={editForm.first_name}
                onChange={onEditChange}
                placeholder="Prénom"
                className={inputClass}
              />
              <input
                type="text"
                name="last_name"
                value={editForm.last_name}
                onChange={onEditChange}
                placeholder="Nom"
                className={inputClass}
              />
              <div className="md:col-span-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className={btnSecondaryClass}
                >
                  {tt("cancel", "Annuler")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={btnPrimaryClass}
                >
                  {tt("save", "Enregistrer")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordModal && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
          onClick={closePasswordModal}
        >
          <div
            className={`${glassPanelClass} w-full max-w-xl p-6 md:p-7`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-xl font-semibold text-ink-900 dark:text-white mb-1">
                  {tt("changePassword", "Changer le mot de passe")}
                </h2>
                <p className="text-sm text-ink-500 dark:text-white/70">
                  {tt("newPasswordFor", "Nouveau mot de passe pour")}{" "}
                  {selectedUser.username}
                </p>
              </div>
              <button
                type="button"
                onClick={closePasswordModal}
                className={btnSecondaryClass}
              >
                {tt("close", "Fermer")}
              </button>
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleResetPassword();
              }}
              className="space-y-4"
            >
              <input
                type="password"
                value={newPasswordInput}
                onChange={(event) => setNewPasswordInput(event.target.value)}
                placeholder="Nouveau mot de passe (min 6)"
                className={inputClass}
                required
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className={btnSecondaryClass}
                >
                  {tt("cancel", "Annuler")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={btnPrimaryClass}
                >
                  {tt("update", "Mettre à jour")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
